import { HourlyRate } from './../../../models/HourlyRate';
import { useSelector } from 'react-redux';
import {
  getCompaniesByIdState,
  getProjectByIdState,
} from '../../../apps/main/rootReducer';
import { CompaniesByIdState } from '../../companies/reducers/companies';
import XLSX from 'xlsx';
import i18n from '../../../i18n';
import { Company } from '../../../models/Company';
import {
  Contact,
  ContactSearchResultItem,
  ContactTag,
} from '../../../models/Contact';
import {
  AdhancedProjectMemberSearchResultItem,
  ProjectMemberSearchResultItem,
} from '../../../models/ProjectContacts';
import { ProjectByIdState } from '../../projects/reducers/projects';
import { exportToCsv } from '../../../util/export';
import useContactsContext from '../hooks/useContactsProvider';
import { ApiResult } from '../../../api';

export const exportContactsToExcelFile: (
  contacts: Contact[],
  companiesById: CompaniesByIdState
) => void = (contacts, companiesById) => {
  const contactsToExport = contacts.map((contact) => {
    const office = companiesById[contact?.companyId]?.offices.find(
      (_office) => _office.officeId === contact.officeId
    );

    return {
      [i18n.t(`contacts:form.labels.firstName`)]: contact.firstName,
      [i18n.t(`contacts:form.labels.lastName`)]: contact.lastName,
      [i18n.t(`contacts:form.labels.title`)]: contact.title,
      [i18n.t(`contacts:form.labels.gender`)]:
        contact.gender === 'male'
          ? i18n.t(`contacts:form.gender.male`)
          : contact.gender === 'female'
            ? i18n.t(`contacts:form.gender.female`)
            : i18n.t(`contacts:form.gender.diverse`),
      [i18n.t(`contacts:form.labels.eMail`)]: contact.eMail,
      [i18n.t(`contacts:form.labels.eMail2`)]: contact.eMail2,
      [i18n.t(`contacts:form.labels.phone`)]: contact.phone,
      [i18n.t(`contacts:form.labels.phone2`)]: contact.phone2,
      [i18n.t(`contacts:form.labels.cellphone`)]: contact.cellphone,
      [i18n.t(`contacts:form.labels.cellphone2`)]: contact.cellphone2,
      [i18n.t(`contacts:form.labels.fax`)]: contact.fax,
      [i18n.t(`contacts:form.labels.notes`)]: contact.notes,
      [i18n.t(`contacts:form.labels.contactType`)]:
        contact.contactType === 'InternalContact'
          ? i18n.t(`contacts:form.contactType.internalContact`)
          : i18n.t(`contacts:form.contactType.externalContact`),
      [i18n.t(`contacts:form.labels.companyName`)]:
        companiesById[contact.companyId]?.fullName,
      [i18n.t(`contacts:form.labels.street`)]: office?.street,
      [i18n.t(`contacts:form.labels.zip`)]: office?.zip,
      [i18n.t(`contacts:form.labels.city`)]: office?.city,
      [i18n.t(`contacts:form.labels.federalStateCode`)]:
        office?.federalStateCode,
      [i18n.t(`contacts:form.labels.countryCode`)]: office?.countryCode,
    };
  });

  var wb = XLSX.utils.book_new();
  var ws = XLSX.utils.json_to_sheet(contactsToExport);

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    i18n.t('contacts:exportList.contactListTitle')
  );
  XLSX.writeFile(wb, `${i18n.t('contacts:exportList.contactListTitle')}.xlsx`);
};

export const exportCompaniesToExcelFile: (companies: Company[]) => void = (
  companies
) => {
  const companiesToExport = companies.map((company) => {
    const mainOffice = company?.offices?.find((o) => o.isMainOffice);
    const firstBankAccount =
      company.bankAccounts.length > 0 ? company.bankAccounts[0] : null;

    return {
      [i18n.t(`companies:form.labels.shortName`)]: company.shortName,
      [i18n.t(`companies:form.labels.fullName`)]: company.fullName,
      [i18n.t(`companies:form.labels.fullName2`)]: company.fullName2,
      [i18n.t(`companies:form.labels.website`)]: company.website,
      [i18n.t(`companies:form.labels.notes`)]: company.notes,
      [i18n.t(`companies:companyType.title`)]:
        company.companyType === 'internalCompany'
          ? i18n.t(`companies:companyType.internalCompany`)
          : company.companyType === 'externalCompany'
            ? i18n.t(`companies:companyType.externalCompany`)
            : i18n.t(`companies:companyType.contactCompany`),
      [i18n.t(`companies:form.labels.eMail`)]: mainOffice?.eMail,
      [i18n.t(`companies:form.labels.phone`)]: mainOffice?.phone,
      [i18n.t(`companies:form.labels.street`)]: mainOffice?.street,
      [i18n.t(`companies:form.labels.zip`)]: mainOffice?.zip,
      [i18n.t(`companies:form.labels.city`)]: mainOffice?.city,
      [i18n.t(`companies:form.labels.federalStateCode`)]:
        mainOffice?.federalStateCode,
      [i18n.t(`companies:form.labels.countryCode`)]: mainOffice?.countryCode,
      [i18n.t(`companies:internalCompanyForm.labels.creditInstitutionName`)]:
        firstBankAccount?.creditInstitutionName,
      [i18n.t(`companies:internalCompanyForm.labels.bic`)]:
        firstBankAccount?.bic,
      [i18n.t(`companies:internalCompanyForm.labels.iban`)]:
        firstBankAccount?.iban,
    };
  });

  var wb = XLSX.utils.book_new();
  var ws = XLSX.utils.json_to_sheet(companiesToExport);

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    i18n.t('contacts:exportList.companyListTitle')
  );
  XLSX.writeFile(wb, `${i18n.t('contacts:exportList.companyListTitle')}.xlsx`);
};

export const exportCompanyContactsToExcelFile: (
  contacts: ContactSearchResultItem[],
  companiesById: CompaniesByIdState,
  contactTags: ApiResult<ContactTag[]>
) => void = (contacts, companiesById, contactTags) => {
  const contactsToExport = contacts.map(({ data, calculated }) => {
    const office = companiesById[data.contact?.companyId]?.offices.find(
      (_office) => _office.officeId === data.contact.officeId
    );

    return {
      [i18n.t(`contacts:form.labels.firstName`)]: data.contact?.firstName ?? '',
      [i18n.t(`contacts:form.labels.lastName`)]: data.contact?.lastName ?? '',
      [i18n.t(`contacts:form.labels.title`)]: data.contact?.title ?? '',
      [i18n.t(`contacts:form.labels.gender`)]:
        data.contact?.gender === 'male'
          ? i18n.t(`contacts:form.gender.male`)
          : data.contact?.gender === 'female'
            ? i18n.t(`contacts:form.gender.female`)
            : data.contact?.gender === 'diverse'
              ? i18n.t(`contacts:form.gender.diverse`)
              : '',
      [i18n.t(`contacts:form.labels.eMail`)]: calculated?.email ?? '',
      [i18n.t(`contacts:form.labels.eMail2`)]: data.contact?.eMail2 ?? '',
      [i18n.t(`contacts:form.labels.phone`)]: calculated?.phone ?? '',
      [i18n.t(`contacts:form.labels.phone2`)]: data.contact?.phone2 ?? '',
      [i18n.t(`contacts:form.labels.cellphone`)]: data.contact?.cellphone ?? '',
      [i18n.t(`contacts:form.labels.cellphone2`)]:
        data.contact?.cellphone2 ?? '',
      [i18n.t(`contacts:form.labels.fax`)]: data.contact?.fax ?? '',
      [i18n.t(`contacts:form.labels.notes`)]: data.contact?.notes ?? '',
      [i18n.t(`contacts:form.labels.contactType`)]:
        data.contact?.contactType === 'InternalContact'
          ? i18n.t(`contacts:form.contactType.internalContact`)
          : data.contact?.contactType === 'ExternalContact'
            ? i18n.t(`contacts:form.contactType.externalContact`)
            : '',
      [i18n.t(`contacts:form.labels.contactTags`)]: calculated.contactTagIds
        .map((tagId) => {
          const tag = contactTags?.data?.find((t) => t.contactTagId === tagId);
          return tag?.name;
        })
        .join(', '),
      [i18n.t(`contacts:form.labels.companyName`)]:
        companiesById[data.contact?.companyId]?.fullName ?? '',
      [i18n.t(`contacts:form.labels.street`)]: office?.street ?? '',
      [i18n.t(`contacts:form.labels.zip`)]: office?.zip ?? '',
      [i18n.t(`contacts:form.labels.city`)]: office?.city ?? '',
      [i18n.t(`contacts:form.labels.federalStateCode`)]:
        office?.federalStateCode ?? '',
      [i18n.t(`contacts:form.labels.countryCode`)]: office?.countryCode ?? '',

      [i18n.t(`companies:form.labels.shortName`)]:
        data.company?.shortName ?? '',
      [i18n.t(`companies:form.labels.fullName`)]: data.company?.fullName ?? '',
      [i18n.t(`companies:form.labels.fullName2`)]:
        data.company?.fullName2 ?? '',
      [i18n.t(`companies:form.labels.website`)]: data.company?.website ?? '',
      [i18n.t(`companies:form.labels.notesCompany`)]: data.company?.notes ?? '',
      [i18n.t(`companies:companyType.title`)]:
        data.company?.companyType === 'internalCompany'
          ? i18n.t(`companies:companyType.internalCompany`)
          : data.company?.companyType === 'externalCompany'
            ? i18n.t(`companies:companyType.externalCompany`)
            : data.company?.companyType === 'ContactCompany'
              ? i18n.t(`companies:companyType.contactCompany`)
              : '',
      [i18n.t(`companies:form.labels.address`)]: calculated?.address ?? '',
    };
  });

  var wb = XLSX.utils.book_new();
  var ws = XLSX.utils.json_to_sheet(contactsToExport);

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    i18n.t('contacts:exportList.contactListTitle')
  );
  XLSX.writeFile(wb, `${i18n.t('contacts:title')}.xlsx`);
};

export function useExportContactsToExcelFile(): (contacts: Contact[]) => void {
  const companiesById = useSelector(getCompaniesByIdState);

  return (contacts: Contact[]) =>
    exportContactsToExcelFile(contacts, companiesById);
}
export function useExportCompaniesToExcelFile(): (
  companies: Company[]
) => void {
  return (companies: Company[]) => exportCompaniesToExcelFile(companies);
}
export function useExportContactCompaniesToExcelFile(): (
  contacts: ContactSearchResultItem[],
  contactTags: ApiResult<ContactTag[]>
) => void {
  const companiesById = useSelector(getCompaniesByIdState);

  return (
    contacts: ContactSearchResultItem[],
    contactTags: ApiResult<ContactTag[]>
  ) => exportCompanyContactsToExcelFile(contacts, companiesById, contactTags);
}

export const exportProjectMembersToCsv: (
  projectMembers:
    | ProjectMemberSearchResultItem[]
    | AdhancedProjectMemberSearchResultItem[],
  projectsById: ProjectByIdState,
  getContactById: (contactId: string) => Contact | null,
  hourlyRates: HourlyRate[]
) => void = (projectMembers, projectsById, getContactById, hourlyRates) => {
  const isEnhancedType = (
    item: AdhancedProjectMemberSearchResultItem | ProjectMemberSearchResultItem
  ): item is AdhancedProjectMemberSearchResultItem => 'entry' in item;

  const dataRows: string[][] = projectMembers.map((projectMember) => {
    if (projectMember) {
      const { projectId, contactId, hourlyRateId, jobTitle, projectRoles } =
        isEnhancedType(projectMember)
          ? projectMember.entry.data
          : projectMember.data;

      const { projectExtensionAccessProjectExtensionMaps: extensionMaps } =
        isEnhancedType(projectMember)
          ? projectMember.entry.calculated
          : projectMember.calculated;

      const project = projectId ? projectsById[projectId] : null;
      const contact = contactId ? getContactById(contactId) : null;
      const hourlyRate = hourlyRates?.find(
        (rate) => rate.hourlyRateId === hourlyRateId
      );
      return [
        project?.name,
        hourlyRate?.name,
        jobTitle,

        projectRoles
          .map((pr) =>
            i18n.t(`contacts:projectContactsPage.projectRoles.${pr}`)
          )
          .join(', '),
        contact?.firstName,
        contact?.lastName,
        extensionMaps
          .map((map) =>
            map.shouldHaveAccess === true ? map.projectExtensionType : null
          )
          .filter((type) => type !== null)
          .join(', ') ?? '',
      ];
    }
    return [];
  });

  const headers = [
    i18n.t('contacts:projectContactsPage.columns.projectName'),
    i18n.t('contacts:projectContactsPage.columns.hourlyRate'),
    i18n.t('contacts:projectContactsPage.columns.jobTitle'),
    i18n.t('contacts:projectContactsPage.columns.projectRoles'),
    i18n.t('contacts:projectContactsPage.columns.firstName'),
    i18n.t('contacts:projectContactsPage.columns.lastName'),
    i18n.t('contacts:projectContactsPage.columns.addIns'),
  ];
  exportToCsv(headers, dataRows, i18n.t('contacts:title'));
};

export function useExportProjectMembersToCsv(): (
  projectMembers:
    | ProjectMemberSearchResultItem[]
    | AdhancedProjectMemberSearchResultItem[],
  hourlyRates: HourlyRate[]
) => void {
  const projectsById = useSelector(getProjectByIdState);

  const { getContactById } = useContactsContext();

  return (
    projectMembers:
      | ProjectMemberSearchResultItem[]
      | AdhancedProjectMemberSearchResultItem[],
    hourlyRates: HourlyRate[]
  ) =>
    exportProjectMembersToCsv(
      projectMembers,
      projectsById,
      getContactById,
      hourlyRates
    );
}
